import React from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {useNavigate} from 'react-router';
import {getMyCarDetail} from '@di-shared/api/cci/auth';
import {diApi} from '@di-shared/api/di';
import {ERROR} from '@di-shared/const';
import useApiError from '@di-shared/features/common/useApiError';
import useCcWebviewUtilsHelper from '@di-shared/features/common/useCcWebviewUtilsHelper';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import environmentStore from '@di-shared/features/environment/environmentStore';
import useOdometer from '@di-shared/features/mileage/useOdometer';
import {ROUTE_PATHS} from '@di-shared/routes';
import {formatNumberWithCommas} from '@di-shared/utils';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import useUnitConverter from '@di-shared/utils/useUnitConverter';
import {ccWebviewUtils} from '@shared/cci/features/webviewUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import usePendingHandler from '@shared/usePendingHandler';
import {useSuspenseQuery} from '@tanstack/react-query';

interface CarInfoProps {
  vin: string;
}

function CarInfo({vin}: CarInfoProps) {
  return (
    <div className="section_container">
      <ErrorBoundary fallback={<CarInfoOdometerError />}>
        <CarInfoOdometer vin={vin} />
      </ErrorBoundary>
      <div className="car_info_content">
        <ErrorBoundary fallback={<></>}>
          <CarInfoVehicleName vin={vin} />
        </ErrorBoundary>
        <ErrorBoundary fallback={<></>}>
          <CarInfoUpdatedDate vin={vin} />
        </ErrorBoundary>
      </div>
    </div>
  );
}

export default CarInfo;

export function CarInfoLoading() {
  return (
    <div
      className="section_container skeleton"
      style={{height: 124}}
    />
  );
}

interface CarInfoOdometerProps {
  vin: string;
}

function CarInfoOdometer({vin}: CarInfoOdometerProps) {
  const {t} = useInferableTranslation();
  const {resolveError, getErrorCode} = useApiError();
  const {odometer, updatedDate, refetch: refetchOdometer} = useOdometer(vin);
  const {convertDistanceSymbol, convertDistanceValue, revertDistanceValue} = useUnitConverter();
  const {openConfirm} = useCcWebviewUtilsHelper();
  const navigate = useNavigate();
  const puid = environmentStore.getState().puid;

  const {data: userRole} = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.vehicle.getVehicle), vin, puid],
    queryFn: () => diApi.vehicle.getVehicle({vin, puid}),
    select: (data) => data.role.code,
  });

  const [updateOdometer] = usePendingHandler(
    async (newOdometer) => {
      try {
        await diApi.maintenancesSestController.updateOdometer({
          vin,
          requestBody: {value: revertDistanceValue(newOdometer)},
        });
      } catch (error: unknown) {
        const errorCode = getErrorCode(error);
        if (errorCode === 4000 && newOdometer) {
          const isConfirm = await openConfirm({
            title: t('T30155'),
            message: t('T30605_$odometer', {
              odometer: `${formatNumberWithCommas(convertDistanceValue(newOdometer))}${convertDistanceSymbol(newOdometer)}`,
            }),
            okText: t('T30592'),
            cancelText: t('T30593'),
          });
          if (isConfirm) {
            navigate(ROUTE_PATHS.DIAG_EXCHANGE_HISTORY.getFullPath(vin));
            return;
          }
          await openUpdateOdometerBottomSheet();
          return;
        }
        throw error;
      }
    },
    async (error) => {
      const [execute] = await resolveError(error);
      execute();
    }
  );

  async function openUpdateOdometerBottomSheet() {
    const newOdometer = await ccWebviewUtils.openBottomSheetWebViewUrl<number>(
      ROUTE_PATHS.BOTTOM_SHEET_DIAG_UPDATE_ODOMETER.getFullPath(),
      {isOdometerDataExist: !!updatedDate, odometer, updatedAt: updatedDate}
    );
    if (!newOdometer) {
      return;
    }
    await updateOdometer(newOdometer);
    await refetchOdometer();
  }

  return (
    <div className="section_container_header">
      <h3 className="blind">{t('T30151')}</h3>
      <strong className="car_info_title">
        <span className="car_info_title_key">{t('T30152')}</span>
        <span className="car_info_title_value">
          {formatNumberWithCommas(convertDistanceValue(odometer))}
          {convertDistanceSymbol(odometer)}
        </span>
      </strong>
      {/* 누적주행거리 수정기능 -> case1인 유저만 표출*/}
      {userRole === 1 && (
        <button
          type="button"
          className="car_info_edit_btn"
          onClick={() => openUpdateOdometerBottomSheet()}
        >
          <span className="ic ic_edit_16px"></span>
          <span className="blind">{t('T30155')}</span>
        </button>
      )}
    </div>
  );
}

function CarInfoOdometerError() {
  const {t} = useInferableTranslation();
  const {defaultDistanceSymbol} = useUnitConverter();
  return (
    <div className="section_container_header">
      <h3 className="blind">{t('T30151')}</h3>
      <strong className="car_info_title">
        <span className="car_info_title_key">{t('T30152')}</span>
        <span className="car_info_title_value">
          {ERROR.DASH}&nbsp;{defaultDistanceSymbol}
        </span>
      </strong>
    </div>
  );
}

interface CarInfoVehicleNameProps {
  vin: string;
}

function CarInfoVehicleName({vin}: CarInfoVehicleNameProps) {
  const {data: myCarDetailData} = useSuspenseQuery({
    queryKey: [getQueryKey(getMyCarDetail), vin],
    queryFn: () => getMyCarDetail(vin),
  });
  const {vehicleNameView, vehicleRegistrationNo} = myCarDetailData;
  return (
    <div className="car_info_detail">
      <span className="car_info_item">{vehicleNameView}</span>
      {vehicleNameView && vehicleRegistrationNo && <div className="divider divider_vertical" />}
      <span className="car_info_item">{vehicleRegistrationNo}</span>
    </div>
  );
}

interface CarInfoUpdatedDateProps {
  vin: string;
}

function CarInfoUpdatedDate({vin}: CarInfoUpdatedDateProps) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {updatedDate} = useOdometer(vin);
  return (
    <span className="car_info_date">
      {t('T30001')} : {updatedDate && CldrDatetimeFormatter.format(updatedDate, 'yMMMd')}
    </span>
  );
}
